import { useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import EditableResumePreview from '../components/EditableResumePreview';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  Upload, FileText, Sparkles, Wand2, Crown, ArrowLeft,
  ArrowRight, Loader2, RefreshCw, Check, Shield, Clock, Lock,
  Linkedin, Target, Copy, CheckCircle2, AlertCircle, TrendingUp, X
} from 'lucide-react';
import { toast } from 'sonner';
import { getAuthToken } from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simplified modes
const REFINE_MODES = [
  {
    id: 'light',
    icon: Sparkles,
    title: 'Fix Errors',
    description: 'Fix grammar, spelling, and punctuation errors only',
    isPremium: false,
  },
  {
    id: 'strong',
    icon: Wand2,
    title: 'Enhance Impact',
    description: 'Strengthen weak verbs and improve clarity',
    isPremium: true,
  },
];

export const RefinePage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [resumeId, setResumeId] = useState(null);
  const [selectedMode, setSelectedMode] = useState('light');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [editableData, setEditableData] = useState(null);
  const [premiumStatus, setPremiumStatus] = useState(null);
  
  // LinkedIn Generator state
  const [showLinkedIn, setShowLinkedIn] = useState(false);
  const [linkedInData, setLinkedInData] = useState(null);
  const [linkedInLoading, setLinkedInLoading] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  
  // Job Match state
  const [showJobMatch, setShowJobMatch] = useState(false);
  const [jobDescription, setJobDescription] = useState('');
  const [jobMatchData, setJobMatchData] = useState(null);
  const [jobMatchLoading, setJobMatchLoading] = useState(false);

  const handleFileUpload = useCallback(async (e) => {
    const uploadedFile = e.target.files?.[0];
    if (!uploadedFile) return;

    const fileExt = uploadedFile.name.split('.').pop()?.toLowerCase();
    if (!['pdf', 'docx'].includes(fileExt)) {
      toast.error('Only PDF and DOCX files are supported');
      return;
    }

    setFile(uploadedFile);
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);

      const token = getAuthToken();
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API}/resumes/upload`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: formData,
      });

      const data = await response.json();
      if (response.ok) {
        setResumeId(data.resume_id);
        toast.success('Resume uploaded successfully');
        setStep(2);
      } else {
        toast.error(data.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error('Failed to upload resume');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRefine = async () => {
    if (!resumeId) return;
    setLoading(true);

    try {
      const token = getAuthToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API}/resumes/refine`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ resume_id: resumeId, mode: selectedMode }),
      });

      const data = await response.json();
      if (response.ok) {
        setResult(data);
        // Set editable data from the structured response
        setEditableData(data.editable_data || null);
        setPremiumStatus(data.premium_status);
        
        // Show warning if premium mode was downgraded
        if (data.premium_required) {
          toast.info(`${selectedMode} mode requires Premium. Applied "strong" mode instead.`);
        } else {
          toast.success('Resume refined successfully');
        }
        setStep(4);
      } else {
        toast.error(data.detail || 'Refinement failed');
      }
    } catch (error) {
      toast.error('Failed to refine resume');
    } finally {
      setLoading(false);
    }
  };

  const handleDataChange = (newData) => {
    setEditableData(newData);
  };

  const handleSaveEdits = (savedData) => {
    setEditableData(savedData);
    toast.success('Your changes have been saved!');
  };

  // LinkedIn Generator
  const handleGenerateLinkedIn = async () => {
    if (!resumeId) return;
    setLinkedInLoading(true);
    setShowLinkedIn(true);
    
    try {
      const token = getAuthToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API}/resumes/linkedin-generator`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ resume_id: resumeId }),
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        setLinkedInData(data.linkedin);
        toast.success('LinkedIn content generated!');
      } else {
        toast.error(data.detail || 'Failed to generate LinkedIn content');
      }
    } catch (error) {
      toast.error('Failed to generate LinkedIn content');
    } finally {
      setLinkedInLoading(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Job Match Analysis
  const handleJobMatch = async () => {
    if (!resumeId || !jobDescription.trim()) {
      toast.error('Please paste a job description');
      return;
    }
    
    setJobMatchLoading(true);
    
    try {
      const token = getAuthToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API}/resumes/job-match`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ resume_id: resumeId, job_description: jobDescription }),
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        setJobMatchData(data.job_match);
        toast.success('Job match analysis complete!');
      } else {
        toast.error(data.detail || 'Failed to analyze job match');
      }
    } catch (error) {
      toast.error('Failed to analyze job match');
    } finally {
      setJobMatchLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-amber-100';
    return 'bg-red-100';
  };

  // Handle back navigation based on current step
  const handleBack = () => {
    if (step === 1) {
      // At upload step, go to home
      navigate('/home');
    } else if (step === 2) {
      // At mode selection, go back to upload
      setStep(1);
    } else if (step === 3) {
      // During processing, go back to mode selection
      setStep(2);
    } else if (step === 4) {
      // At review step, go back to mode selection (resume already uploaded)
      setStep(2);
      setResult(null);
      setEditableData(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={handleBack}
          data-testid="back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {step === 1 ? 'Back to Home' : step === 4 ? 'Try Different Mode' : 'Back'}
        </Button>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-muted-foreground mb-2">
            <span>Step {step} of 4</span>
            <span>{step === 1 ? 'Upload' : step === 2 ? 'Select Mode' : step === 3 ? 'Processing' : 'Review & Edit'}</span>
          </div>
          <Progress value={(step / 4) * 100} className="h-2" />
        </div>

        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="animate-fade-in">
            <div className="text-center mb-8">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-2">
                Refine Your Resume
              </h1>
              <p className="text-muted-foreground">
                Upload your resume to get started
              </p>
            </div>

            <Card className="max-w-xl mx-auto border-slate-100">
              <CardContent className="p-8">
                <label className="block cursor-pointer">
                  <input
                    type="file"
                    accept=".pdf,.docx"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="file-upload-input"
                  />
                  <div className="border-2 border-dashed border-slate-200 rounded-xl p-12 text-center hover:border-primary hover:bg-slate-50 transition-all">
                    {loading ? (
                      <div className="flex flex-col items-center">
                        <Loader2 className="h-12 w-12 text-primary animate-spin mb-4" />
                        <p className="text-muted-foreground">Processing your resume...</p>
                      </div>
                    ) : (
                      <>
                        <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-lg font-medium text-primary mb-2">
                          Drop your resume here
                        </p>
                        <p className="text-sm text-muted-foreground">
                          or click to browse (PDF or DOCX)
                        </p>
                        <div className="flex items-center justify-center gap-2 mt-4 text-xs text-green-600">
                          <Shield className="h-3.5 w-3.5" />
                          <span>Your resume is processed securely and never shared with third parties.</span>
                        </div>
                      </>
                    )}
                  </div>
                </label>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 2: Select Mode */}
        {step === 2 && (
          <div className="animate-fade-in">
            <div className="text-center mb-8">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-2">
                Choose Refinement Style
              </h1>
              <p className="text-muted-foreground">
                How would you like to improve your resume?
              </p>
            </div>

            <div className="max-w-3xl mx-auto">
              {file && (
                <div className="flex items-center gap-3 mb-6 p-4 bg-slate-50 rounded-lg">
                  <FileText className="h-5 w-5 text-primary" />
                  <span className="font-medium text-primary">{file.name}</span>
                  <Check className="h-4 w-4 text-success ml-auto" />
                </div>
              )}

              <RadioGroup 
                value={selectedMode} 
                onValueChange={setSelectedMode}
                className="grid md:grid-cols-2 gap-4"
              >
                {REFINE_MODES.map((mode) => {
                  const Icon = mode.icon;
                  return (
                    <Label
                      key={mode.id}
                      htmlFor={mode.id}
                      className="cursor-pointer"
                    >
                      <Card 
                        className={`border-2 transition-all ${
                          selectedMode === mode.id 
                            ? 'border-primary bg-primary/5' 
                            : 'border-slate-100 hover:border-slate-200'
                        }`}
                        data-testid={`mode-${mode.id}`}
                      >
                        <CardContent className="p-6 flex items-start gap-4">
                          <RadioGroupItem value={mode.id} id={mode.id} className="mt-1" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Icon className="h-5 w-5 text-primary" />
                              <span className="font-medium text-primary">{mode.title}</span>
                              {mode.isPremium && (
                                <Badge className="bg-amber-100 text-amber-700 text-xs">
                                  <Crown className="h-3 w-3 mr-1" />
                                  Premium
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {mode.description}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </Label>
                  );
                })}
              </RadioGroup>

              <div className="flex justify-between mt-8">
                <Button 
                  variant="outline" 
                  onClick={() => setStep(1)}
                  data-testid="step2-back-btn"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                <Button 
                  onClick={() => { setStep(3); handleRefine(); }}
                  data-testid="step2-next-btn"
                >
                  Refine Resume
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Processing */}
        {step === 3 && (
          <div className="animate-fade-in text-center py-16">
            <Loader2 className="h-16 w-16 text-primary animate-spin mx-auto mb-6" />
            <h2 className="font-heading text-2xl font-bold text-primary mb-2">
              Refining Your Resume
            </h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              Our AI is analyzing and improving your resume without changing any facts or achievements...
            </p>
          </div>
        )}

        {/* Step 4: Results with Editable Preview */}
        {step === 4 && result && (
          <div className="animate-fade-in">
            <div className="text-center mb-8">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-2">
                Review & Edit Your Resume
              </h1>
              <p className="text-muted-foreground">
                Click "Edit Resume" to make changes before downloading
              </p>
              {result.premium_required && (
                <div className="mt-4 inline-flex items-center gap-2 text-sm bg-amber-50 text-amber-700 px-4 py-2 rounded-lg">
                  <Lock className="h-4 w-4" />
                  <span>Premium mode applied "strong" refinement. <Link to="/upgrade" className="underline font-medium">Upgrade for advanced modes</Link></span>
                </div>
              )}
            </div>

            {/* Premium Status Banner */}
            {premiumStatus && !premiumStatus.is_premium && (
              <div className="max-w-4xl mx-auto mb-6">
                <Card className="border-amber-200 bg-amber-50">
                  <CardContent className="py-3 px-4 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-amber-700">
                      <Clock className="h-4 w-4" />
                      <span>Downloads remaining today: <strong>{premiumStatus.downloads_remaining}</strong> of 2</span>
                    </div>
                    <Link to="/upgrade">
                      <Badge className="bg-primary hover:bg-primary/90 cursor-pointer">
                        <Crown className="h-3 w-3 mr-1" />
                        Upgrade for Unlimited
                      </Badge>
                    </Link>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* No changes needed message */}
            {result.no_changes_needed && (
              <Card className="max-w-4xl mx-auto border-green-200 bg-green-50 mb-6">
                <CardContent className="py-4 px-6 flex items-center gap-3">
                  <Check className="h-6 w-6 text-green-600" />
                  <div>
                    <p className="font-medium text-green-700">Your resume looks great!</p>
                    <p className="text-sm text-green-600">No grammar or spelling errors found.</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Changes Made - only show if there are changes */}
            {result.changes_made && result.changes_made.length > 0 && (
              <Card className="max-w-4xl mx-auto border-slate-100 mb-6">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-primary" />
                    {result.changes_made.length} Error{result.changes_made.length > 1 ? 's' : ''} Fixed
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[180px]">
                    <div className="space-y-2">
                      {result.changes_made.map((change, index) => (
                        <div key={index} className="text-sm p-3 bg-slate-50 rounded-lg">
                          <div className="text-red-500 line-through text-xs">{change.original}</div>
                          <div className="text-green-600 font-medium mt-1">→ {change.refined}</div>
                          <div className="text-xs text-muted-foreground mt-1 italic">{change.reason}</div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            )}

            {/* NEW: Additional Tools Section */}
            <div className="max-w-4xl mx-auto mb-6">
              <div className="grid md:grid-cols-2 gap-4">
                {/* LinkedIn Generator Card */}
                <Card className="border-blue-200 hover:border-blue-300 transition-all cursor-pointer"
                      onClick={() => !linkedInData && handleGenerateLinkedIn()}
                      data-testid="linkedin-generator-card">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Linkedin className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-primary">LinkedIn Generator</h3>
                        <p className="text-xs text-muted-foreground">Get optimized headline & summary</p>
                      </div>
                    </div>
                    {linkedInLoading ? (
                      <div className="flex items-center gap-2 text-sm text-blue-600">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Generating...
                      </div>
                    ) : linkedInData ? (
                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setShowLinkedIn(true); }}>
                        View Results
                      </Button>
                    ) : (
                      <p className="text-xs text-blue-600">Click to generate →</p>
                    )}
                  </CardContent>
                </Card>

                {/* Job Match Card */}
                <Card className="border-green-200 hover:border-green-300 transition-all cursor-pointer"
                      onClick={() => setShowJobMatch(true)}
                      data-testid="job-match-card">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                        <Target className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-primary">Job Match Score</h3>
                        <p className="text-xs text-muted-foreground">See how well you match a job</p>
                      </div>
                    </div>
                    {jobMatchData ? (
                      <div className="flex items-center gap-2">
                        <Badge className={`${getScoreBg(jobMatchData.match_score)} ${getScoreColor(jobMatchData.match_score)}`}>
                          {jobMatchData.match_score}% Match
                        </Badge>
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setShowJobMatch(true); }}>
                          View Details
                        </Button>
                      </div>
                    ) : (
                      <p className="text-xs text-green-600">Click to analyze →</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* LinkedIn Results Modal */}
            {showLinkedIn && linkedInData && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="max-w-2xl w-full max-h-[90vh] overflow-auto">
                  <CardHeader className="border-b sticky top-0 bg-white z-10">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <Linkedin className="h-5 w-5 text-blue-600" />
                        LinkedIn Content
                      </CardTitle>
                      <Button variant="ghost" size="sm" onClick={() => setShowLinkedIn(false)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6 space-y-6">
                    {/* Headline */}
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground mb-2 block">Headline</Label>
                      <div className="relative">
                        <div className="p-3 bg-slate-50 rounded-lg pr-12">
                          <p className="font-medium text-primary">{linkedInData.headline}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="absolute right-2 top-1/2 -translate-y-1/2"
                          onClick={() => copyToClipboard(linkedInData.headline, 'headline')}
                        >
                          {copiedField === 'headline' ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>

                    {/* Alternative Headlines */}
                    {linkedInData.headline_alternatives?.length > 0 && (
                      <div>
                        <Label className="text-sm font-medium text-muted-foreground mb-2 block">Alternative Headlines</Label>
                        <div className="space-y-2">
                          {linkedInData.headline_alternatives.map((alt, i) => (
                            <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                              <span>{alt}</span>
                              <Button size="sm" variant="ghost" onClick={() => copyToClipboard(alt, `alt-${i}`)}>
                                {copiedField === `alt-${i}` ? <CheckCircle2 className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Summary */}
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground mb-2 block">About / Summary</Label>
                      <div className="relative">
                        <div className="p-4 bg-slate-50 rounded-lg pr-12">
                          <p className="text-sm text-primary whitespace-pre-wrap">{linkedInData.summary}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="absolute right-2 top-2"
                          onClick={() => copyToClipboard(linkedInData.summary, 'summary')}
                        >
                          {copiedField === 'summary' ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>

                    {/* Key Skills */}
                    {linkedInData.key_skills?.length > 0 && (
                      <div>
                        <Label className="text-sm font-medium text-muted-foreground mb-2 block">Suggested Skills to Add</Label>
                        <div className="flex flex-wrap gap-2">
                          {linkedInData.key_skills.map((skill, i) => (
                            <Badge key={i} variant="outline">{skill}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Job Match Modal */}
            {showJobMatch && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="max-w-2xl w-full max-h-[90vh] overflow-auto">
                  <CardHeader className="border-b sticky top-0 bg-white z-10">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <Target className="h-5 w-5 text-green-600" />
                        Job Match Analysis
                      </CardTitle>
                      <Button variant="ghost" size="sm" onClick={() => setShowJobMatch(false)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    {!jobMatchData ? (
                      <div className="space-y-4">
                        <div>
                          <Label className="text-sm font-medium mb-2 block">Paste Job Description</Label>
                          <Textarea
                            value={jobDescription}
                            onChange={(e) => setJobDescription(e.target.value)}
                            placeholder="Paste the full job description here..."
                            className="min-h-[200px]"
                            data-testid="job-description-input"
                          />
                        </div>
                        <Button 
                          onClick={handleJobMatch} 
                          disabled={jobMatchLoading || !jobDescription.trim()}
                          className="w-full"
                          data-testid="analyze-match-btn"
                        >
                          {jobMatchLoading ? (
                            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Analyzing...</>
                          ) : (
                            <><Target className="mr-2 h-4 w-4" /> Analyze Match</>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {/* Score Display */}
                        <div className="text-center">
                          <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full ${getScoreBg(jobMatchData.match_score)}`}>
                            <span className={`text-3xl font-bold ${getScoreColor(jobMatchData.match_score)}`}>
                              {jobMatchData.match_score}%
                            </span>
                          </div>
                          <p className={`mt-2 font-semibold ${getScoreColor(jobMatchData.match_score)}`}>
                            {jobMatchData.verdict}
                          </p>
                        </div>

                        {/* Score Breakdown */}
                        {jobMatchData.score_breakdown && (
                          <div className="grid grid-cols-2 gap-3">
                            {Object.entries(jobMatchData.score_breakdown).map(([key, value]) => (
                              <div key={key} className="p-3 bg-slate-50 rounded-lg">
                                <p className="text-xs text-muted-foreground capitalize">{key.replace('_', ' ')}</p>
                                <div className="flex items-center gap-2">
                                  <Progress value={value} className="h-2 flex-1" />
                                  <span className="text-sm font-medium">{value}%</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Matching Keywords */}
                        {jobMatchData.matching_keywords?.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-green-700 mb-2 flex items-center gap-1">
                              <CheckCircle2 className="h-4 w-4" /> Matching Keywords
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {jobMatchData.matching_keywords.map((kw, i) => (
                                <Badge key={i} className="bg-green-100 text-green-700">{kw}</Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Missing Keywords */}
                        {jobMatchData.missing_keywords?.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-red-700 mb-2 flex items-center gap-1">
                              <AlertCircle className="h-4 w-4" /> Missing Keywords (Add to Resume)
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {jobMatchData.missing_keywords.map((kw, i) => (
                                <Badge key={i} className="bg-red-100 text-red-700">{kw}</Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Suggestions */}
                        {jobMatchData.suggestions?.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-primary mb-2 flex items-center gap-1">
                              <TrendingUp className="h-4 w-4" /> Suggestions to Improve
                            </p>
                            <ul className="space-y-2">
                              {jobMatchData.suggestions.map((sug, i) => (
                                <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                                  <ArrowRight className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" />
                                  {sug}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <Button 
                          variant="outline" 
                          className="w-full"
                          onClick={() => { setJobMatchData(null); setJobDescription(''); }}
                        >
                          Analyze Another Job
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Editable Resume Preview */}
            <div className="max-w-4xl mx-auto mb-8">
              {editableData ? (
                <EditableResumePreview
                  resumeId={resumeId}
                  initialData={editableData}
                  onSave={handleSaveEdits}
                  onDataChange={handleDataChange}
                  source="refine"
                />
              ) : (
                <Card className="border-slate-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-heading flex items-center gap-2">
                      <FileText className="h-5 w-5 text-primary" />
                      Refined Resume
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[400px]">
                      <pre className="whitespace-pre-wrap text-sm text-primary font-body">
                        {result.refined}
                      </pre>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-4 justify-center mb-6">
              <Button 
                variant="outline"
                onClick={() => { setStep(2); setResult(null); setEditableData(null); }}
                data-testid="regenerate-btn"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Another Style
              </Button>
              <Button
                onClick={() => {
                  // Save refined content and edited data, then navigate to template selection
                  sessionStorage.setItem('refinedResumeId', resumeId);
                  sessionStorage.setItem('refinedContent', result.refined);
                  // IMPORTANT: Save edited data so ConvertPage can use it
                  if (editableData) {
                    sessionStorage.setItem('refinedEditableData', JSON.stringify(editableData));
                  }
                  navigate('/convert', { state: { fromRefine: true, resumeId, refinedContent: result.refined } });
                }}
                data-testid="convert-to-template-btn"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Choose Template & Download
              </Button>
            </div>

            {/* Info card about template selection */}
            <Card className="max-w-2xl mx-auto border-blue-200 bg-blue-50">
              <CardContent className="py-4 px-6">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-blue-700">Your content has been refined!</p>
                    <p className="text-xs text-blue-600">Select a professional template to download your polished resume</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default RefinePage;
